import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface FilterPopoverProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  triggerRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

export function FilterPopover({
  open,
  onClose,
  title,
  triggerRef,
  children,
  align = 'left',
  className = '',
}: FilterPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 639px)').matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(max-width: 639px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    setIsMobileViewport(mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!open) return;

    // Lock background scrolling while mobile popover is open
    const originalOverflow = document.body.style.overflow;
    if (isMobileViewport) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        (!triggerRef?.current || !triggerRef.current.contains(target))
      ) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      if (isMobileViewport) {
        document.body.style.overflow = originalOverflow;
        document.body.classList.remove('modal-open');
      }
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open, onClose, triggerRef, isMobileViewport]);

  if (!open) return null;

  const backdrop = (
    <div
      className="fixed inset-0 z-40 bg-black/50 transition-opacity"
      style={{
        animation: 'filter-backdrop-fade 220ms cubic-bezier(0.16, 1, 0.3, 1) both',
      }}
      onClick={onClose}
      aria-hidden="true"
    />
  );

  const dialog = (
    <div
      ref={popoverRef}
      role="dialog"
      aria-modal="true"
      className={`
        fixed inset-x-3 bottom-4 z-50 max-h-[82dvh]
        sm:absolute sm:inset-auto sm:top-full sm:mt-2 sm:bottom-auto sm:z-50 sm:max-h-none
        sm:min-w-[320px] sm:max-w-[420px]
        bg-bg-raised/95 border border-border-default rounded-xl shadow-2xl shadow-black/60 backdrop-blur-xl
        p-4 text-text-primary flex flex-col
        ${align === 'right' ? 'sm:right-0 sm:left-auto' : 'sm:left-0 sm:right-auto'}
        ${className}
      `}
      style={{
        colorScheme: 'dark',
        animation: 'filter-popover-slide-up 240ms cubic-bezier(0.16, 1, 0.3, 1) both',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between pb-2 mb-3 border-b border-border-subtle shrink-0">
        <div className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
          {title || 'Filter'}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close filter"
          className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 max-h-[70dvh] sm:max-h-[50vh] overflow-y-auto pr-0.5">
        {children}
      </div>
    </div>
  );

  const keyframesStyle = (
    <style>{`
      @keyframes filter-backdrop-fade {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes filter-popover-slide-up {
        from {
          opacity: 0;
          transform: translateY(28px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `}</style>
  );

  if (isMobileViewport && typeof document !== 'undefined') {
    return createPortal(
      <>
        {keyframesStyle}
        {backdrop}
        {dialog}
      </>,
      document.body
    );
  }

  return (
    <>
      {keyframesStyle}
      {typeof document !== 'undefined' ? createPortal(backdrop, document.body) : backdrop}
      {dialog}
    </>
  );
}

